// SPEC-27 · the navigation pill on top of Conversa, Avaliações and Perfil
// (NavPill of the design system): the PYF logo, which is not a link, the
// three tabs, the active one white with the segment shadow, and her
// initials, which open Perfil like its tab. It scrolls with the page, and
// the questionnaires do not show it.

import { Avatar } from "./Avatar";
import { PyfLogo } from "./PyfLogo";
import { useCopy, useLanguageFade } from "../i18n/LanguageProvider";
import { paths, useRouter } from "../lib/router";
import type { Path } from "../lib/router";

export type Tab = "conversa" | "assessments" | "profile";

const TABS: readonly { tab: Tab; path: Path }[] = [
  { tab: "conversa", path: paths.conversa },
  { tab: "assessments", path: paths.assessments },
  { tab: "profile", path: paths.profile },
];

const segment =
  "flex h-[var(--touch-min)] min-w-0 items-center rounded-pill px-3 text-body-sm " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf";

export function NavPill({ active, firstName }: { active: Tab; firstName: string }) {
  const { navigate } = useRouter();
  const text = useCopy().nav;
  // The words fade after a change of language; the logo and her initials hold still.
  const fade = useLanguageFade();
  const open = (tab: Tab, path: Path) => {
    if (tab !== active) navigate(path);
  };

  return (
    <nav aria-label={text.label} className="flex items-center justify-between gap-1 px-5 pt-4">
      <PyfLogo className="w-9 shrink-0 text-dark" />
      <div className={`${fade} flex min-w-0 items-center`}>
        {TABS.map(({ tab, path }) => (
          <button
            key={tab}
            type="button"
            aria-current={tab === active ? "page" : undefined}
            onClick={() => open(tab, path)}
            className={
              tab === active
                ? `${segment} bg-white font-semibold text-dark shadow-segment`
                : `${segment} font-medium text-muted hover:text-dark`
            }
          >
            {text[tab]}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => open("profile", paths.profile)}
        className="flex size-[var(--touch-min)] shrink-0 items-center justify-center rounded-pill"
      >
        <Avatar firstName={firstName} size="small" />
        <span className="sr-only">{text.openProfile}</span>
      </button>
    </nav>
  );
}
